import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AccessRequestClientModel, useApproveAccessRequest, useRejectAccessRequest } from '@client-app-sdk';
import ConfirmDialog from '@common/ui/ConfirmDialog';
import { AnalyticsEvents } from '@common/utils/analytics/events';

interface RequestApprovalProps {
  request: AccessRequestClientModel;
  onApproval?: () => void;
}

export default function RequestApproval({ request, onApproval }: RequestApprovalProps) {
  return (
    <Stack direction="row" spacing={2} justifyContent="flex-end">
      <RejectAction request={request} onApproval={onApproval} />
      <ApproveAction request={request} onApproval={onApproval} />
    </Stack>
  );
}

function RejectAction({ request, onApproval }: RequestApprovalProps) {
  const { rejectAccessRequest, isLoading, isSuccess } = useRejectAccessRequest(request.id);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Request ${request.id} rejected`);
      onApproval?.();
    }
  }, [isSuccess, onApproval, request.id]);

  return (
    <>
      <LoadingButton
        data-trigger={AnalyticsEvents.TaskRejectClicked}
        variant="outlined"
        size="small"
        onClick={() => setOpened(true)}
        loading={isLoading}
      >
        Reject
      </LoadingButton>
      <ConfirmDialog
        open={opened}
        onClose={() => setOpened(false)}
        onConfirm={() => rejectAccessRequest({})}
        confirmLabel="Yes, reject"
      >
        Are you sure you want to reject request <strong>{request.id}</strong>?
      </ConfirmDialog>
    </>
  );
}

function ApproveAction({ request, onApproval }: RequestApprovalProps) {
  const { approveAccessRequest, isLoading, isSuccess } = useApproveAccessRequest(request.id);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Request ${request.id} approved`);
      onApproval?.();
    }
  }, [isSuccess, onApproval, request.id]);

  return (
    <>
      <LoadingButton
        data-trigger={AnalyticsEvents.TaskApproveClicked}
        variant="contained"
        size="small"
        onClick={() => setOpened(true)}
        loading={isLoading}
      >
        Approve
      </LoadingButton>
      <ConfirmDialog
        open={opened}
        onClose={() => setOpened(false)}
        onConfirm={() => approveAccessRequest({})}
        confirmLabel="Yes, approve"
      >
        Are you sure you want to approve request <strong>{request.id}</strong>?
      </ConfirmDialog>
    </>
  );
}
